import { useMemo } from "react";
import { AgGridReact } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetGroupsQuery } from "~/graphql/mainHooks";
import { type PermissionRow } from "~/pages/UserAndRoles/WorkspacesPage/types";

type GroupsData = ReturnType<typeof useGetGroupsQuery>["data"];

export const usePermissionsTableAgGridOptions = () => {
  const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = useMemo(() => {
    return [
      {
        field: "permissionName",
        headerName: "Permission",
        filter: "agTextColumnFilter",
      },
    ];
  }, []);

  return { columnDefs };
};

export const useFetchPermissions = (title: string | undefined, groupsData?: GroupsData) => {
  const useGetGroupsQueryResult = useGetGroupsQuery({
    context: withClientContext("staff"),
    skip: !!groupsData,
  });

  const rowData = useMemo(() => {
    if (!title) {
      return [];
    }

    const groups = groupsData?.groups ?? useGetGroupsQueryResult.data?.groups;
    if (!groups?.edges) {
      return [];
    }

    const matchingGroup = groups.edges.find((edge) => {
      return edge.node?.title?.toLowerCase() === title.toLowerCase();
    });

    const roles = matchingGroup?.node?.roles?.edges;
    if (!Array.isArray(roles)) {
      return [];
    }

    const permissions: PermissionRow[] = roles
      .filter((role) => typeof role?.node?.name === "string" && role.node.name.length > 0)
      .map((role) => ({
        permissionName: role.node.name!,
      }));

    return permissions;
  }, [title, groupsData, useGetGroupsQueryResult.data]);

  return {
    rowData,
    usePermissionsQueryResult: useGetGroupsQueryResult,
  };
};
